import React, {useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Flex, Text, Divider, Box, Alert, RadioGroup} from '@spothero/ui';
import FormatUtils from 'utils/format';
import {Controller} from 'react-hook-form';
import {
    PROPTYPE_RATE_DIFF,
    PROPTYPE_RESERVATION,
    PROPTYPE_RESERVATION_VEHICLE,
} from '@/proptypes/reservation-prop-types';
import BorderedRadio from 'common/bordered-radio';
import {scrollToRef} from 'utils/dom-utils';
import {useDispatch, useSelector} from 'react-redux';
import {updateVehicle} from 'store/parking-pass/parking-pass-actions';
import UserUtils, {PAYPAL_WALLET_VALUE} from 'utils/user-utils';
import {getCardInitials} from 'utils/payment-utils';
import RefundBreakdown from './RefundBreakdown';

export const REFUND_FIELD = 'refund';
export const REFUND_FORM_ID = 'REFUND_FORM_ID';
export const CREDIT_VALUE = 'CREDIT_VALUE';
export const ORIGINAL_PAYMENT_METHOD_VALUE = 'ORIGINAL_PAYMENT_METHOD';
const NO_SELECTION_ERROR = 'NO_SELECTION_ERROR';
const GENERIC_ERROR = 'GENERIC_ERROR';

const ERROR_MAPPING = {
    [NO_SELECTION_ERROR]: {
        title: 'Please choose where to send your refund',
    },
    [GENERIC_ERROR]: {
        title: 'Oops, we hit a snag issuing your refund',
        description: 'Please try again.',
    },
};

export const textStyle = {
    fontSize: 'sm',
    lineHeight: '1.4',
    color: 'text.secondary.light',
};

const OversizeRefund = ({
    rateDiff,
    handleSubmit,
    handleSuccess,
    control,
    setError,
    error,
    reservation,
    vehicle,
    register,
    handleReservationVehicleUpdated,
    handleReservationVehicleUpdateFailed,
}) => {
    const {
        priceDifference,
        priceBreakdown: {refundAmount, price},
        originalRental: {
            price: originalRentalPrice,
            amountCharged,
            discountAmount,
            spotheroCreditUsed,
        },
    } = rateDiff;

    const {creditCard: reservationCreditCard, rentalId} = reservation;
    const errorMessageRef = useRef(null);
    const dispatch = useDispatch();
    const {accessKey} = useSelector(state => state.parkingPass);
    const {status: userStatus} = useSelector(state => state.user.data);
    const oversizeAmount = originalRentalPrice - price;

    const refundedToCardFormatted = FormatUtils.price(refundAmount.charge)
        .displayPrice;
    const refundedToCreditFormatted = FormatUtils.price(refundAmount.credit)
        .displayPrice;
    const totalRefundFormatted = FormatUtils.price(
        refundAmount.credit + refundAmount.charge
    ).displayPrice;

    const refundCard = reservationCreditCard;
    const onlyCreditView = refundCard?.deleted || refundAmount.charge === 0;
    const noCreditOptionView = userStatus !== UserUtils.AUTH_STATE.USER;

    const cardDisplay = useMemo(() => {
        if (!refundCard) {
            return 'Original Payment Method';
        }

        if (refundCard?.wallet === PAYPAL_WALLET_VALUE) {
            return `Paypal ${refundCard.paypalEmail}`;
        } else {
            const cardTypeFormatted = FormatUtils.slugify(refundCard.type);

            return (
                <>
                    {getCardInitials(cardTypeFormatted)}
                    <sup>*</sup>
                    {refundCard.last4}
                </>
            );
        }
    }, [refundCard]);

    const onSubmit = async ({refund: refundCardValue}) => {
        if (!refundCardValue) {
            handleReservationVehicleUpdateFailed({
                priceDifference,
                newVehicle: vehicle,
                view: 'refund-oversize',
                errors: [{message: 'Please choose where to send your refund'}],
            });

            setError(REFUND_FIELD, {
                type: 'custom',
                message: NO_SELECTION_ERROR,
            });
            scrollToRef(errorMessageRef);

            return;
        }

        const response = await dispatch(
            updateVehicle({
                vehicleProfileId: vehicle.vehicleProfileId,
                reservationId: rentalId,
                price,
                cardExternalId: null,
                refundAsCredit: refundCardValue === CREDIT_VALUE,
                guestVehicle: vehicle,
                accessKey,
            })
        );

        if (response.success) {
            handleReservationVehicleUpdated({
                priceDifference,
                newVehicle: vehicle,
                view: 'refund-oversize',
            });
            handleSuccess(oversizeAmount * -1);
        } else {
            handleReservationVehicleUpdateFailed({
                priceDifference,
                newVehicle: vehicle,
                view: 'refund-oversize',
                errors: [{message: 'Oops, we hit a snag issuing your refund'}],
            });

            setError(REFUND_FIELD, {
                type: 'custom',
                message: GENERIC_ERROR,
            });
            scrollToRef(errorMessageRef);
        }
    };

    if (noCreditOptionView) {
        return (
            <Flex width="100%" flexDirection="column">
                <Box
                    as="form"
                    onSubmit={handleSubmit(onSubmit)}
                    id={REFUND_FORM_ID}
                >
                    <input
                        {...register(REFUND_FIELD, {
                            shouldUnregister: true,
                            value: ORIGINAL_PAYMENT_METHOD_VALUE,
                        })}
                        type="hidden"
                    />
                </Box>
                <RefundBreakdown
                    amountCharged={amountCharged}
                    discountAmount={discountAmount}
                    spotheroCreditUsed={spotheroCreditUsed}
                    originalRentalPrice={originalRentalPrice}
                    oversizeAmount={oversizeAmount}
                >
                    <Text {...textStyle} color="success" fontWeight="semibold">
                        Amount to be refunded:
                    </Text>
                    <Text
                        {...textStyle}
                        justifySelf="end"
                        color="success"
                        fontWeight="semibold"
                    >
                        ${refundedToCardFormatted}
                    </Text>
                </RefundBreakdown>
                <Text fontSize="sm" color="text.secondary.light">
                    Your refund will be sent to your original payment method. It
                    typically takes 5-10 business days for it to appear on your
                    payment method.
                </Text>
            </Flex>
        );
    }

    if (onlyCreditView) {
        return (
            <Flex width="100%" flexDirection="column">
                <Box
                    as="form"
                    onSubmit={handleSubmit(onSubmit)}
                    id={REFUND_FORM_ID}
                >
                    <input
                        {...register(REFUND_FIELD, {
                            shouldUnregister: true,
                            value: CREDIT_VALUE,
                        })}
                        type="hidden"
                    />
                </Box>
                <RefundBreakdown
                    amountCharged={amountCharged}
                    discountAmount={discountAmount}
                    spotheroCreditUsed={spotheroCreditUsed}
                    originalRentalPrice={originalRentalPrice}
                    oversizeAmount={oversizeAmount}
                >
                    <Text
                        marginTop={6}
                        {...textStyle}
                        color="success"
                        fontWeight="semibold"
                    >
                        Fee refund to SpotHero Credit:
                    </Text>
                    <Text
                        justifySelf="end"
                        marginTop={6}
                        {...textStyle}
                        fontWeight="semibold"
                        color="success"
                    >
                        ${totalRefundFormatted}
                    </Text>
                </RefundBreakdown>
            </Flex>
        );
    }

    return (
        <Flex width="100%" flexDirection="column">
            <RefundBreakdown
                amountCharged={amountCharged}
                discountAmount={discountAmount}
                spotheroCreditUsed={spotheroCreditUsed}
                originalRentalPrice={originalRentalPrice}
                oversizeAmount={oversizeAmount}
            >
                {refundAmount.credit > 0 && (
                    <>
                        <Text
                            {...textStyle}
                            color="success"
                            fontWeight="semibold"
                        >
                            SpotHero Credit to refund:
                        </Text>
                        <Text
                            justifySelf="end"
                            {...textStyle}
                            fontWeight="semibold"
                            color="success"
                        >
                            ${refundedToCreditFormatted}
                        </Text>
                    </>
                )}
                <Text {...textStyle} color="success" fontWeight="semibold">
                    {refundAmount.credit > 0
                        ? 'Remaining to refund:'
                        : 'Amount to be refunded:'}
                </Text>
                <Text
                    justifySelf="end"
                    {...textStyle}
                    color="success"
                    fontWeight="semibold"
                >
                    ${refundedToCardFormatted}
                </Text>
            </RefundBreakdown>

            <Divider width="100%" color="gray.100" />
            <Box
                as="form"
                sx={{
                    '.chakra-form-control': {
                        paddingX: 0,
                        marginX: 0,
                        paddingTop: 0,
                    },
                    '.chakra-form__label': {
                        fontWeight: 'semibold',
                        color: 'black',
                        fontSize: 'base',
                        paddingTop: 6,
                        paddingBottom: 4,
                    },
                }}
                onSubmit={handleSubmit(onSubmit)}
                id={REFUND_FORM_ID}
            >
                <Controller
                    name={REFUND_FIELD}
                    control={control}
                    render={({field}) => (
                        <RadioGroup
                            sx={{
                                '.chakra-radio__control': {
                                    marginTop: 0.5,
                                },
                                '.chakra-radio__label': {
                                    overflow: 'hidden',
                                },
                                '.chakra-stack div:not(:first-child)': {
                                    marginTop: 3,
                                },
                            }}
                            marginBottom={4}
                            label="Where should we send your refund?"
                            id={REFUND_FIELD}
                            {...field}
                        >
                            <BorderedRadio
                                isSelected={field.value === CREDIT_VALUE}
                                value={CREDIT_VALUE}
                                radioStyles={{
                                    paddingY: 3,
                                    height: 'auto',
                                }}
                            >
                                <Text marginBottom={1}>SpotHero Credit</Text>
                                <Text color="text.secondary.light">
                                    Credit will be issued immediately
                                </Text>
                            </BorderedRadio>
                            <BorderedRadio
                                isSelected={
                                    field.value ===
                                    ORIGINAL_PAYMENT_METHOD_VALUE
                                }
                                value={ORIGINAL_PAYMENT_METHOD_VALUE}
                                radioStyles={{
                                    paddingY: 3,
                                    height: 'auto',
                                }}
                            >
                                <Text marginBottom={1}>{cardDisplay}</Text>
                                <Text color="text.secondary.light">
                                    It may take 5&ndash;10 business days for the
                                    refund to appear on your account.
                                </Text>
                            </BorderedRadio>
                        </RadioGroup>
                    )}
                />

                {error && (
                    <Alert
                        ref={errorMessageRef}
                        title={ERROR_MAPPING[error.message].title}
                        description={ERROR_MAPPING[error.message].description}
                        showIcon
                        status="error"
                        size="md"
                    />
                )}
            </Box>
        </Flex>
    );
};

OversizeRefund.propTypes = {
    rateDiff: PROPTYPE_RATE_DIFF,
    error: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    handleSubmit: PropTypes.func.isRequired,
    handleSuccess: PropTypes.func.isRequired,
    control: PropTypes.object.isRequired,
    setError: PropTypes.func.isRequired,
    register: PropTypes.func.isRequired,
    reservation: PROPTYPE_RESERVATION,
    vehicle: PROPTYPE_RESERVATION_VEHICLE,
    handleReservationVehicleUpdated: PropTypes.func.isRequired,
    handleReservationVehicleUpdateFailed: PropTypes.func.isRequired,
};

export default OversizeRefund;
